import { useMemo } from 'react';

import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';

import { ForbiddenPage } from '@/components/errors/ForbiddenPage';
import { AppHeaderUserControls } from '@/components/layouts/AppHeaderUserControls';
import { PageLoading } from '@/components/loading/PageLoading';
import { env } from '@/config/env';
import { PATHS } from '@/config/paths';
import { useEffectiveMenuPermissions } from '@/features/auth/hooks/useEffectiveMenuPermissions';
import { useGetMyMenuTreeQuery } from '@/features/menus/api/menusApi';
import { OpsDynamicMenuNav } from '@/features/menus/components/OpsDynamicMenuNav';
import { extractOpsMenuTree } from '@/features/menus/utils/menuTreeFilters';
import {
  filterOpsSidebarHiddenPaths,
  getMergedAccessibleOpsPaths,
} from '@/features/navigation/opsMenuPaths';
import { useAppSelector } from '@/hooks/useAppSelector';

/**
 * Sidebar Vận hành: cây từ `GET /api/menus/me` (nhánh `/ops/...` hoặc `metadata.nav_scope` trên node).
 * Cấu hình mục qua admin `POST /api/menus` — xem `MenuAdminCreateBody` trong `@/features/menus/types/menu.types`.
 * Không có `VITE_API_BASE_URL`: không tải menu; sidebar gợi ý, vẫn render outlet (dev).
 */
export function OpsLayout() {
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.auth.user);
  const { data: menuPermissions } = useEffectiveMenuPermissions();
  const skipMenu = !user || !env.API_BASE_URL;
  const {
    data: menuTree,
    isSuccess: menuSuccess,
    isFetching: menuFetching,
    isError: menuError,
  } = useGetMyMenuTreeQuery(undefined, {
    skip: skipMenu,
  });

  const opsFromApi = useMemo(() => {
    if (!menuSuccess || !menuTree?.length) return [];
    return filterOpsSidebarHiddenPaths(extractOpsMenuTree(menuTree));
  }, [menuSuccess, menuTree]);

  const mergedAccessiblePaths = useMemo(
    () => getMergedAccessibleOpsPaths(user, menuPermissions, menuError ? undefined : menuTree),
    [user, menuPermissions, menuTree, menuError],
  );

  const apiMenuPending = Boolean(env.API_BASE_URL && user && menuFetching);
  /** Dựa trên merge tĩnh + động — tránh Forbidden khi chỉ có route tĩnh (vd. ma trận phân quyền). */
  const showForbidden =
    !skipMenu && !apiMenuPending && menuSuccess && !menuError && mergedAccessiblePaths.length === 0;

  if (showForbidden) {
    return <ForbiddenPage />;
  }

  return (
    <div className="ops-layout-root">
      <header className="ops-layout-header">
        <a href="#ops-main-content" className="ops-skip-link">
          {t('ops.skipToContent')}
        </a>
        <div className="ops-layout-header-inner">
          <NavLink to={PATHS.HOME} className="ops-back-link">
            <ArrowLeft size={16} strokeWidth={2.2} aria-hidden />
            {t('ops.backToMap')}
          </NavLink>
          <div className="ops-header-divider" aria-hidden />
          <span className="ops-layout-title">{t('ops.title')}</span>
        </div>
        <div className="ops-layout-header-actions">
          <AppHeaderUserControls />
        </div>
      </header>

      <div className="ops-layout-body">
        <aside className="ops-layout-sidebar" aria-label={t('ops.navAria')}>
          {skipMenu ? (
            <p style={{ margin: '12px', fontSize: 12, color: 'var(--text-muted)' }}>
              {t('ops.menuApiRequiredHint')}
            </p>
          ) : apiMenuPending && opsFromApi.length === 0 ? (
            <div className="ops-sidebar-loading">
              <PageLoading />
            </div>
          ) : (
            <nav className="ops-sidebar-nav">
              <OpsDynamicMenuNav tree={opsFromApi} />
            </nav>
          )}
        </aside>

        <main id="ops-main-content" className="ops-layout-main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
